/* @flow */

import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { NetgemApiEmitterType, RequestResponseMethodDefinitionType } from '../emitter';

import { logError } from '../../../../helpers/debug/debug';
import { createCustomNetworkErrorFromKey } from '../../../../libs/netgemLibrary/helpers/CreateCustomNetworkError';
import { CustomNetworkError } from '../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { HeaderName, HeaderValue } from '../../../../libs/netgemLibrary/v8/constants/Headers';
import { HttpStatus } from '../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { updateBOFavoriteList } from '../../../ui/actions';
import { REDUX_MSG_REQUEST_BO_FAVORITE_LIST } from '../../constants';
import getRequestDetails from './helpers/requestCommon';
import { BO_API_FAVORITE_LIST, type BO_RESPONSE_WITH_ETAG } from './types/common';

const getFavoriteList: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers, NetgemApiEmitter: NetgemApiEmitterType): Promise<void> => {
    const {
      appConfiguration: { mainDistributorId },
      ui: { favoriteListETag },
    } = getState();

    if (!mainDistributorId) {
      return Promise.reject(
        createCustomNetworkErrorFromKey('common.messages.errors.missing_parameters_list', {
          list: 'mainDistributorId',
          name: 'Get favorite list',
        }),
      );
    }

    // Object 'requestDetails' contains 'headers', 'method' and 'uri' parameters
    const requestDetails = getRequestDetails(getState, mainDistributorId, BO_API_FAVORITE_LIST, [HeaderName.Accept, HeaderValue.ApplicationJson]);

    return NetgemApiEmitter.emit(REDUX_MSG_REQUEST_BO_FAVORITE_LIST, {
      ...requestDetails,
      eTag: favoriteListETag,
      signal,
    })
      .then(({ eTag, response }: BO_RESPONSE_WITH_ETAG) => {
        if (signal?.aborted) {
          return;
        }

        // BO returns HTTP 204 when list is empty (hence result is null)
        const favorites = response?.body.map((f) => f.data.itemId.toString()) ?? [];

        dispatch(updateBOFavoriteList(favorites, eTag));
      })
      .catch((error: Error | CustomNetworkError) => {
        if (error instanceof CustomNetworkError && error.getStatus() !== HttpStatus.NotModified) {
          logError(`Error retrieving favorite list for "${mainDistributorId}": ${error.message}`);
        }
      });
  };

export default getFavoriteList;
